import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Mail, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, CONTACT_INFO } from "@/data/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50">
      <div className="hidden bg-corp-700 lg:block" data-testid="header-utility-bar">
        <div className="mx-auto flex h-9 max-w-7xl items-center justify-between px-6 lg:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
            DSIR-Approved R&D • Advanced Genetics • Higher Yields
          </p>
          <div className="flex items-center gap-6 text-[11px] font-medium text-white/85">
            <a href={`mailto:${CONTACT_INFO.email}`} data-testid="header-email" className="flex items-center gap-1.5 transition-colors hover:text-gold-300">
              <Mail className="h-3 w-3" /> {CONTACT_INFO.email}
            </a>
            <a href={`tel:${CONTACT_INFO.phone}`} data-testid="header-phone" className="flex items-center gap-1.5 transition-colors hover:text-gold-300">
              <Phone className="h-3 w-3" /> {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </div>

      <div
        className={`border-b border-[#123C6B]/10 bg-white/95 backdrop-blur-xl transition-shadow duration-500 ${
          scrolled ? "shadow-[0_14px_44px_rgba(18,60,107,0.12)]" : ""
        }`}
      >
        <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
            {NAV_LINKS.slice(1, 6).map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `group relative py-2 text-[13px] font-bold tracking-wide transition-colors ${
                    isActive ? "text-brand-400" : "text-foreground hover:text-corp-500"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute inset-x-0 -bottom-0.5 h-0.5 origin-left bg-brand-400 transition-transform duration-300 ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="group flex items-center gap-1.5 bg-brand-400 px-6 py-3 text-[12px] font-bold tracking-wide text-white transition-all hover:bg-brand-500 hover:shadow-[0_10px_26px_rgba(242,112,29,0.35)]"
            >
              Contact Us
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href="https://msgroupin.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-ms-group-link"
              aria-label="An MS Group company — visit msgroupin.com"
              title="An MS Group Company"
              className="ml-1 flex items-center border-l border-[#123C6B]/15 pl-4 transition-opacity hover:opacity-80"
            >
              <img src="/assets/ms-group-logo.png" alt="MS Group" className="h-8 w-auto" />
            </a>
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-foreground lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        <div className="h-0.5 w-full bg-gradient-to-r from-brand-400 via-gold-300 to-corp-500" />
      </div>

      {open && (
        <nav data-testid="mobile-nav" className="border-b border-[#123C6B]/10 bg-white px-6 py-6 lg:hidden">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 font-display text-xl font-medium tracking-tight ${
                    isActive ? "text-brand-400" : "text-foreground"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
